/**
 * encodeViaWorker — high-level helper that combines the per-doc update log
 * with the client-side Yjs Web Worker to deliver "encode this doc's full
 * state" without a synchronous main-thread encode call.
 *
 * Mirrors `websocket-server/src/yjs/encodeViaPool.js`.
 *
 * Why this layer exists
 * ─────────────────────
 * The 80% browser use case is "I have a doc with an attached log, give me
 * its encoded state via the worker." Wraps that pattern so call sites
 * (notably the IDB stale-check that historically froze the UI) stay short
 * and consistent.
 *
 * Failure modes
 * ─────────────
 *   - If the worker is disabled (kill-switch via VITE_YJS_WEB_WORKER_ENABLED
 *     or localStorage), the client falls back to synchronous main-thread
 *     dispatch. We pay the encode cost on main thread, but the API is
 *     unchanged so callers don't branch.
 *   - If the worker throws (crash, timeout), this helper re-throws.
 *     `encodeDocViaWorkerBestEffort` swallows and returns null instead.
 *     Either way, the log's `repair()` is called first so a failed
 *     encode never leaves the log holding buffers the worker already
 *     detached — see the try/catch in `encodeDocViaWorker` below.
 *
 * Reference: `/workspace/.cursor/plans/websocket_robustness_refactor_b886ced2.plan.md`
 */

import type { DocUpdateLog } from './docUpdateLog';
import { getYjsWorkerClient } from './yjsWorkerClient';

interface YjsWorkerClientLike {
	encodeFromUpdates(updates: Uint8Array[]): Promise<Uint8Array>;
}

// Serializes concurrent `encodeDocViaWorker` calls against the SAME log.
//
// `log.snapshot()` returns direct references to the log's Uint8Arrays (by
// design — copying them would reintroduce the main-thread blocking cost this
// worker architecture exists to avoid). Those buffers are transferred (and
// thus detached) to the worker via `postMessage(..., transferList)`. If two
// callers race — e.g. crdtServiceWS's `idb.once('synced')` and
// `provider.once('sync')` handlers both encoding the same doc's log — the
// second caller's `snapshot()` can capture buffer objects the first caller
// already transferred, and the browser throws
// "ArrayBuffer at index 0 is already detached" on the second postMessage.
//
// Chaining onto any in-flight run for the same log guarantees a second
// caller's `snapshot()` never executes until the first caller's `replace()`
// has already collapsed the log — so no two calls can ever hold overlapping
// buffer references. On the (overwhelmingly common) non-concurrent path this
// costs nothing more than chaining onto an already-resolved promise.
const inFlight = new WeakMap<DocUpdateLog, Promise<Uint8Array>>();

/**
 * Encode a doc's full state via the Web Worker, using its attached log.
 * On success, the log is compacted (replaced with the consolidated state)
 * unless `compact: false` is passed.
 *
 * Safe to call concurrently on the same `log` — see `inFlight` above.
 */
export async function encodeDocViaWorker(
	log: DocUpdateLog,
	options: { compact?: boolean; client?: YjsWorkerClientLike } = {},
): Promise<Uint8Array> {
	const { compact = true, client = getYjsWorkerClient() } = options;

	if (!log || typeof log.snapshot !== 'function') {
		throw new TypeError('encodeDocViaWorker: log must be a docUpdateLog');
	}

	const prior = inFlight.get(log) ?? Promise.resolve();
	const run = prior.catch(() => {}).then(async () => {
		// Capture both the updates AND the generation marker so concurrent
		// updates that arrive on the doc's listener while the worker is
		// running aren't lost when we collapse the log. See
		// `docUpdateLog.replace(state, generation)` for the full contract.
		const { updates, generation } = log.snapshot();
		try {
			const encoded = await client.encodeFromUpdates(updates);

			if (compact && !log.isDestroyed()) {
				log.replace(encoded, generation);
			}

			return encoded;
		} catch (err) {
			// `client.encodeFromUpdates` transfers (and thus detaches) each
			// buffer in `updates` via `postMessage(..., transferList)`
			// BEFORE the worker ever replies — so a failure here (timeout,
			// crash, thrown error) almost always means those buffers are
			// already dead. Without repair, the log keeps holding those
			// detached `Uint8Array`s forever: the next chained caller's
			// `snapshot()` would hand them straight back to the worker,
			// which fails immediately with "ArrayBuffer ... is already
			// detached" — a permanently poisoned log for the rest of this
			// doc's session (see Sentry `JAVASCRIPT-REACT-BP` and siblings).
			// Re-seeding from the live doc (unaffected by the failed
			// transfer) recovers a clean log for whoever calls next.
			if (!log.isDestroyed()) {
				log.repair();
			}
			throw err;
		}
	});

	inFlight.set(log, run);
	try {
		return await run;
	} finally {
		// Only clear the slot if we're still the most recent run — an even
		// later caller may have already overwritten it with their own chain.
		if (inFlight.get(log) === run) {
			inFlight.delete(log);
		}
	}
}

/**
 * Best-effort variant: returns null on worker failure instead of throwing.
 * Use for fire-and-forget call sites where a worker hiccup shouldn't break
 * the surrounding code (e.g. the IDB stale-check; if it fails, we just
 * skip the optimization and continue).
 */
export async function encodeDocViaWorkerBestEffort(
	log: DocUpdateLog,
	options: {
		compact?: boolean;
		client?: YjsWorkerClientLike;
		logger?: { warn?: (...args: unknown[]) => void };
		logTag?: string;
	} = {},
): Promise<Uint8Array | null> {
	const { logger = console, logTag = '[encodeViaWorker]' } = options;
	try {
		return await encodeDocViaWorker(log, options);
	} catch (err) {
		logger.warn?.(`${logTag} encode failed: ${(err as Error).message}`);
		return null;
	}
}
